import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, Coffee, Dumbbell, Clock, Mic, Sparkles } from "lucide-react";
import { LiquidGlass } from "@/components/ui/liquid-glass";
// Background gradients synced with notification index
const BACKGROUND_GRADIENTS = [
  "linear-gradient(135deg, #2d1b69 0%, #11998e 100%)",
  "linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)",
  "linear-gradient(135deg, #0f3460 0%, #e94560 100%)",
  "linear-gradient(135deg, #134e5e 0%, #71b280 100%)",
  "linear-gradient(135deg, #7d3c00 0%, #b8860b 100%)",
  "linear-gradient(135deg, #3a1c71 0%, #d76d77 100%)",
];

interface Notification {
  id: number;
  name: string;
  message: string;
  time: string;
  icon: React.ReactNode;
  isDark: boolean;
  iconBg: string;
}

const notifications: Notification[] = [
  {
    id: 1,
    name: "Académie Nova",
    message: "Le webinaire commence dans 30 minutes. Cliquez pour rejoindre.",
    time: "Maintenant",
    icon: <GraduationCap className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #5e3a8c 0%, #7b4fa8 100%)", // Education - Elegant academic violet
  },
  {
    id: 2,
    name: "Café du Marché",
    message: "Offre du midi : café offert pour tout déjeuner aujourd'hui.",
    time: "10 min",
    icon: <Coffee className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #5d4037 0%, #8b6b4f 100%)", // Coffee - Warm brown
  },
  {
    id: 3,
    name: "Studio Fit+",
    message: "💪 Des places viennent de se libérer pour le cours de ce soir.",
    time: "25 min",
    icon: <Dumbbell className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #c2185b 0%, #e91e63 100%)", // Fitness - Energetic magenta
  },
  {
    id: 4,
    name: "Julie Martin - Coaching",
    message: "⏰ Rappel : votre session live démarre à 18h.",
    time: "45 min",
    icon: <Clock className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #e89bb3 0%, #d65d8c 100%)", // Coaching - Baby pink to soothing fuchsia
  },
  {
    id: 5,
    name: "CréaCast",
    message: "🎙️ Nouvel épisode disponible. Écoutez-le maintenant.",
    time: "1 h",
    icon: <Mic className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #bf360c 0%, #e65100 100%)", // Podcast - Orange/amber media
  },
  {
    id: 6,
    name: "Boutique L'Épure",
    message: "✨ Nouvelle collection en boutique. Venez découvrir.",
    time: "2 h",
    icon: <Sparkles className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #d4a853 0%, #a68523 100%)", // Fashion - Elegant gold/yellow
  },
];

export function PhoneMockup() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % notifications.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const currentNotification = notifications[activeIndex];
  const isDarkBg = currentNotification.isDark;
  // Get current time for display
  const now = new Date();
  const hours = now.getHours().toString().padStart(2, "0");
  const minutes = now.getMinutes().toString().padStart(2, "0");
  
  // French day and date
  const days = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  const dayName = days[now.getDay()];
  const dayNum = now.getDate();
  const monthName = months[now.getMonth()];

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        setIsHovered(true);
        setIsPaused(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPaused(false);
      }}
    >
      {/* Phone Frame - DaisyUI inspired mockup */}
      <div className="mockup-phone border-[#075d69]">
        <div className="mockup-phone-camera"></div>
        <div className="mockup-phone-display">
          {/* Lockscreen with animated gradient background */}
          <motion.div
            className="w-full h-full flex flex-col items-center pt-12 px-4 relative overflow-hidden"
            animate={{ background: BACKGROUND_GRADIENTS[activeIndex % BACKGROUND_GRADIENTS.length] }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
          >
            {/* Status bar */}
            <motion.div 
              className="absolute top-3 left-0 right-0 flex justify-between items-center px-6 text-xs"
              animate={{ color: isDarkBg ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)" }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <span className="font-medium">{hours}:{minutes}</span>
              <div className="flex items-center gap-1">
                <span className="text-[10px]">•••</span>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 3a9 9 0 0 0-9 9c0 4.97 4.03 9 9 9a9 9 0 0 0 0-18zm0 16c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" opacity="0.3"/>
                  <path d="M12 5c-3.87 0-7 3.13-7 7h2c0-2.76 2.24-5 5-5V5z"/>
                </svg>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="2" y="7" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <rect x="20" y="10" width="2" height="4" rx="0.5"/>
                  <rect x="4" y="9" width="14" height="6" rx="1" opacity="0.8"/>
                </svg>
              </div>
            </motion.div>

            {/* Time */}
            <div className="mt-6 text-center">
              <motion.p 
                className="text-6xl font-light tracking-tight"
                animate={{ color: isDarkBg ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.9)" }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                {hours}:{minutes}
              </motion.p>
              <motion.p 
                className="text-base mt-1"
                animate={{ color: isDarkBg ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.7)" }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                {dayName} {dayNum} {monthName}
              </motion.p>
            </div>

            {/* Notification - iOS Liquid Glass Style */}
            <div className="mt-8 w-full px-3">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentNotification.id}
                  initial={{ y: -20, opacity: 0, scale: 0.95 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  exit={{ y: 10, opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  <LiquidGlass
                    borderRadius="20px"
                    blurIntensity="xl"
                    glowIntensity="xs"
                    shadowIntensity="xs"
                    className="border border-white/20 bg-white/8"
                  >
                    <div className="p-3.5">
                      <div className="flex items-start gap-3">
                        {/* App Icon - Round like iOS */}
                        <div 
                          className="w-10 h-10 rounded-[12px] flex items-center justify-center flex-shrink-0 text-white"
                          style={{
                            background: currentNotification.iconBg,
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                          }}
                        >
                          {currentNotification.icon}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0 pt-0.5">
                          {/* Header row: App name + time */}
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <p 
                              className="text-[15px] tracking-tight leading-tight"
                              style={{ 
                                color: "#FFFFFF",
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
                                fontWeight: 600,
                              }}
                            >
                              {currentNotification.name}
                            </p>
                            <span 
                              className="text-[13px] flex-shrink-0"
                              style={{ 
                                color: "#D9D9D9",
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
                                fontWeight: 400,
                              }}
                            >
                              {currentNotification.time}
                            </span>
                          </div>
                          {/* Message - full display, no truncation, max 2 lines */}
                          <p 
                            className="text-[15px] leading-[1.35]"
                            style={{ 
                              color: "#FFFFFF",
                              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
                              fontWeight: 400,
                            }}
                          >
                            {currentNotification.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  </LiquidGlass>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Pagination dots */}
            <motion.div
              className="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: isHovered ? 1 : 0.3 }}
              transition={{ duration: 0.2 }}
            >
              {notifications.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === activeIndex ? "w-4" : ""
                  }`}
                  animate={{ 
                    backgroundColor: isDarkBg 
                      ? (index === activeIndex ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)") 
                      : (index === activeIndex ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.3)")
                  }}
                  whileHover={{ 
                    backgroundColor: isDarkBg ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)"
                  }}
                  transition={{ duration: 0.3 }}
                  aria-label={`Notification ${index + 1}`}
                />
              ))}
            </motion.div>

            {/* Home indicator */}
            <motion.div 
              className="absolute bottom-3 left-1/2 transform -translate-x-1/2 w-32 h-1 rounded-full"
              animate={{ backgroundColor: isDarkBg ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)" }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
